import { assignInlineVars } from '@vanilla-extract/dynamic'
import { useState } from 'react'
import * as styles from './beacon-coverage.css.ts'
import {
  checkLabel,
  COVERAGE_COLUMNS,
  type CoverageEntity,
  type CoverageModel,
  formatTokens,
  STATUS_LABELS,
} from './coverage-model.ts'

type Row = { entity: CoverageEntity; isChild?: boolean; isExpanded?: boolean; isNotApplicable?: boolean }

const rowTotal = (entity: CoverageEntity) =>
  COVERAGE_COLUMNS.reduce((total, column) => total + (entity.tokensByLens[column] ?? 0), 0)

/**
 * Checks × surface: every part of the attack surface against every check, shaded by the tokens that check
 * spent there (log scale), so a check that skipped a whole area shows as an empty column.
 */
export const BeaconCoverageMatrix: React.FC<{
  model: CoverageModel
  expanded: ReadonlySet<string>
  onToggle: (entryPointKey: string) => unknown
  onSelect: (key: string) => unknown
}> = ({ model, expanded, onToggle, onSelect }) => {
  const topLevel: CoverageEntity[] = [...model.entryPoints, ...model.controls, model.repository]
  const allValues = [...topLevel, ...model.groups.values()]
    .flatMap(entity => COVERAGE_COLUMNS.map(column => entity.tokensByLens[column] ?? 0))
    .filter(value => value > 0)
  const low = Math.log10(Math.min(...allValues, 1))
  const high = Math.log10(Math.max(...allValues, 10))
  const maxRow = Math.max(...topLevel.map(rowTotal), 1)
  const columnTotal = (column: string) =>
    topLevel.reduce((total, entity) => total + (entity.tokensByLens[column] ?? 0), 0)
  const maxColumn = Math.max(...COVERAGE_COLUMNS.map(columnTotal), 1)

  const entryRows: Row[] = model.entryPoints.flatMap(ep => [
    { entity: ep, isExpanded: ep.groupKeys.length ? expanded.has(ep.key) : undefined },
    ...(expanded.has(ep.key)
      ? ep.groupKeys.flatMap(key => {
          const group = model.groups.get(key)
          return group ? [{ entity: group, isChild: true }] : []
        })
      : []),
  ])

  const cell = (entity: CoverageEntity, column: string, isNotApplicable: boolean) => {
    if (column === 'ai_scan_sink_backward' && model.isSinkLensUnrun) {
      return (
        <td
          key={column}
          className={styles.cell({ kind: 'unrun' })}
          title="This check did not run: Beacon could not list the dangerous calls to trace"
        >
          ·
        </td>
      )
    }
    if (isNotApplicable) {
      return (
        <td key={column} className={styles.cell({ kind: 'na' })} title="Not applicable">
          n/a
        </td>
      )
    }
    const value = entity.tokensByLens[column] ?? 0
    if (!value) {
      return (
        <td key={column} className={styles.cell({ kind: 'empty' })}>
          ·
        </td>
      )
    }
    const percent = Math.round(10 + 90 * ((Math.log10(value) - low) / Math.max(1e-9, high - low)))
    return (
      <td
        key={column}
        className={styles.cell({ kind: 'value', isHot: percent > 55 })}
        style={assignInlineVars({ [styles.heat]: `${percent}%` })}
        title={`${entity.name} · ${checkLabel(column)}: ${formatTokens(value)} tokens`}
        data-key={entity.key}
      >
        {formatTokens(value)}
      </td>
    )
  }

  const row = ({ entity, isChild, isExpanded, isNotApplicable = false }: Row) => {
    const total = rowTotal(entity)
    return (
      <tr key={entity.key}>
        <td className={`${styles.nameCell} ${isChild ? styles.childName : ''}`} data-key={entity.key}>
          <div className={styles.nameRow}>
            {isExpanded === undefined ? (
              !isChild && <span className={styles.expanderSpacer} />
            ) : (
              <button
                type="button"
                className={styles.expander}
                aria-expanded={isExpanded}
                aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${entity.name}`}
                onClick={() => onToggle(entity.key)}
              >
                {isExpanded ? '▾' : '▸'}
              </button>
            )}
            <button type="button" className={styles.nameButton} onClick={() => onSelect(entity.key)}>
              <span className={styles.nameText} title={entity.name}>
                {entity.name}
              </span>
            </button>
            {!isChild && entity.status !== 'examined' && (
              <span className={styles.status({ status: entity.status })}>{STATUS_LABELS[entity.status]}</span>
            )}
          </div>
        </td>
        {COVERAGE_COLUMNS.map(column => cell(entity, column, isNotApplicable))}
        <td>{entity.findings.length > 0 && <span className={styles.keptBadge}>{entity.findings.length}</span>}</td>
        <td className={styles.totalCell}>
          <div className={styles.totalBar} style={{ width: `${(total / maxRow) * 100}%` }} />
          <span className={styles.totalLabel}>{total ? formatTokens(total) : '—'}</span>
        </td>
      </tr>
    )
  }

  const section = (label: string) => (
    <tr key={label}>
      <td className={styles.section} colSpan={COVERAGE_COLUMNS.length + 3}>
        {label}
      </td>
    </tr>
  )

  return (
    <>
      <div className={styles.legend}>
        <span className={styles.scale}>
          Tokens spent <i className={styles.scaleBar} /> more
        </span>
        <span className={styles.legendItem}>
          <i className={styles.cell({ kind: 'empty' })} style={{ width: '12px', height: '12px', minWidth: 0 }} />
          Check not run here
        </span>
        <span className={styles.legendItem}>
          <i className={styles.swatch} style={{ background: styles.HATCH_FILL }} />
          Did not run / not applicable
        </span>
        <span className={styles.muted}>Expand an entry point for its operation groups.</span>
      </div>
      <div className={styles.matrixWrap}>
        <table className={styles.matrix} aria-label="Tokens each check spent on each part of the attack surface">
          <thead>
            <tr>
              <th className={`${styles.columnHead} ${styles.rowHead}`} scope="col">
                Part of the attack surface
              </th>
              {COVERAGE_COLUMNS.map(column => (
                <th key={column} className={styles.columnHead} scope="col">
                  {checkLabel(column)}
                  {column === 'ai_scan_sink_backward' && model.isSinkLensUnrun && (
                    <span className={styles.columnNote}>did not run</span>
                  )}
                </th>
              ))}
              <th className={styles.columnHead} scope="col">
                Verified findings
              </th>
              <th className={styles.columnHead} scope="col">
                Total effort
              </th>
            </tr>
          </thead>
          <tbody>
            {section('Entry points')}
            {entryRows.map(row)}
            {section('Security controls')}
            {model.controls.map(control => row({ entity: control, isNotApplicable: !control.isAuditable }))}
            {section('Repository')}
            {row({ entity: model.repository })}
          </tbody>
          <tfoot>
            <tr>
              <td className={styles.totalLabel}>Spent per check</td>
              {COVERAGE_COLUMNS.map(column => {
                const total = columnTotal(column)
                return (
                  <td key={column} className={styles.columnTotal}>
                    <div
                      className={styles.columnBar}
                      style={{ height: `${Math.max(total ? 3 : 0, (total / maxColumn) * 40)}px` }}
                    />
                    <span className={styles.totalLabel}>{total ? formatTokens(total) : '—'}</span>
                  </td>
                )
              })}
              <td />
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
    </>
  )
}

export const useExpandedRows = () => {
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set())
  const toggle = (key: string) =>
    setExpanded(previous => {
      const next = new Set(previous)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  return { expanded, toggle, setExpanded }
}
